import { Suspense, lazy } from "react";
import { Routes, Route, Navigate, useLocation, Outlet } from "react-router-dom";
import ChatPage from "../pages/ChatPage";
import { useAppContext } from "../contexts/AppContext";
import { AnimatePresence, motion } from "framer-motion";

const TermsOfUsePage = lazy(() => import("../pages/TermsOfUsePage"));
const PrivacyPolicyPage = lazy(() => import("../pages/PrivacyPolicyPage"));
const ErrorPage = lazy(() => import("../pages/ErrorPage"));
const RegisterPage = lazy(() => import("../pages/RegisterPage"));
const AssessmentPage = lazy(() => import("../pages/AssessmentPage"));
const AccountPage = lazy(() => import("../pages/AccountPage"));
const PriorityPage = lazy(() => import("../pages/PriorityPage"));
const FeedbackPage = lazy(() => import("../pages/FeedbackPage"));
const ContactPage = lazy(() => import("../pages/ContactPage"));
const ChatNetworkPage = lazy(() => import("../pages/ChatNetworkPage"));
const ConfirmEmail = lazy(() => import("../pages/ConfirmEmail"));
const EmailConfirmed = lazy(() => import("../pages/EmailConfirmed"));
const ConfirmationFailed = lazy(() => import("../pages/ConfirmationFailed"));
const ForgotPassword = lazy(() => import("../pages/ForgotPassword"));
const PasswordReset = lazy(() => import("../pages/PasswordReset"));
const Unsubscribed = lazy(() => import("../pages/Unsubscribed"));
const PlanSelectionV4 = lazy(() => import("../pages/PlanSelectionV4"));
const AdminCRM = lazy(() => import("../pages/Admin/AdminCRM"));
const DeleteAccountPage = lazy(() => import("../pages/DeleteAccountPage"));

const AnimatedOutlet = () => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    transition={{ duration: 0.4, ease: "easeOut" }}
    style={{ backgroundColor: "#121212", height: "100%" }}
  >
    <Outlet />
  </motion.div>
);

const RouteLoadingFallback = () => (
  <div
    style={{
      minHeight: "100vh",
      width: "100%",
      backgroundColor: "#121212",
    }}
  />
);

function AnimatedRoutes() {
  const location = useLocation();
  const { appProps } = useAppContext();

  const renderLazyRoute = (Component) => (
    <Suspense fallback={<RouteLoadingFallback />}>
      <Component appProps={appProps} />
    </Suspense>
  );

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route element={<AnimatedOutlet />}>
          <Route path="/" element={<Navigate to="/chat" replace />} />

          {/* handle custom paths for x */}
          <Route path="/x/:slug" element={<ChatPage />} />

          <Route
            path="/chat"
            // element={
            //   isPreOrUnsubscribed ? <UnsubscribedChatPage /> : <ChatPage />
            // }
            element={<ChatPage />}
          />
          <Route
            path="/chat-network"
            element={renderLazyRoute(ChatNetworkPage)}
          />

          <Route
            path="/terms-of-use"
            element={renderLazyRoute(TermsOfUsePage)}
          />
          <Route
            path="/privacy-policy"
            element={renderLazyRoute(PrivacyPolicyPage)}
          />

          <Route
            path="/register"
            element={renderLazyRoute(RegisterPage)}
          />
          <Route
            path="/assessment"
            element={renderLazyRoute(AssessmentPage)}
          />
          <Route
            path="/assessment/:slug"
            element={renderLazyRoute(AssessmentPage)}
          />
          <Route
            path="/account"
            element={renderLazyRoute(AccountPage)}
          />
          <Route path="/agent-tasks" element={<Navigate to="/chat" replace />} />
          <Route
            path="/confirm-email"
            element={renderLazyRoute(ConfirmEmail)}
          />
          <Route
            path="/email-confirmed"
            element={renderLazyRoute(EmailConfirmed)}
          />
          <Route
            path="/confirmation-failed"
            element={renderLazyRoute(ConfirmationFailed)}
          />
          <Route
            path="/forgot-password"
            element={renderLazyRoute(ForgotPassword)}
          />
          <Route
            path="/password-reset"
            element={renderLazyRoute(PasswordReset)}
          />
          <Route
            path="/delete-account"
            element={renderLazyRoute(DeleteAccountPage)}
          />

          <Route
            path="/priority"
            element={renderLazyRoute(PriorityPage)}
          />
          <Route
            path="/feedback"
            element={renderLazyRoute(FeedbackPage)}
          />
          <Route
            path="/contact"
            element={renderLazyRoute(ContactPage)}
          />
          <Route
            path="/unsubscribed"
            element={renderLazyRoute(Unsubscribed)}
          />
          <Route
            path="/plan-selection"
            element={renderLazyRoute(PlanSelectionV4)}
          />
          <Route
            path="/puerta-web"
            element={renderLazyRoute(AdminCRM)}
          />

          <Route path="*" element={renderLazyRoute(ErrorPage)} />
        </Route>
      </Routes>
    </AnimatePresence>
  );
}

export default AnimatedRoutes;
